import {
  Campaign,
  CampaignInsight,
  CreateCampaignRequest,
  GenerateEmailTemplateResponse,
  TestEmailRequest,
} from '@/types/campaign';
import { ListResponse } from '@/types/common';
import fetchWithAuth from './base';
import { Recipient } from '@/types/message';
import { ConsoleEvent } from '@/types/workflow';

// Campaign CRUD operations
export const createCampaign = async (
  clientId: string,
  data: CreateCampaignRequest
): Promise<Campaign> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/campaign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const getCampaigns = async ({
  clientId,
  startingAfter,
  endingBefore,
  limit,
  search,
  filterSearchParams,
  status,
}: {
  clientId: string;
  startingAfter?: string;
  endingBefore?: string;
  limit?: number;
  search?: string;
  status?: string;
  filterSearchParams?: URLSearchParams;
}): Promise<ListResponse<Campaign>> => {
  const params = new URLSearchParams(filterSearchParams);
  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }
  if (endingBefore) {
    params.append('ending_before', endingBefore);
  }
  if (limit) {
    params.append('limit', limit.toString());
  }
  if (search) {
    params.append('search', search);
  }
  if (status && status !== 'all') {
    params.append('campaign_status', status);
  }

  const response = await fetchWithAuth(`/api/client/${clientId}/campaign?${params.toString()}`, {
    method: 'GET',
  });
  const responseData = await response.json();

  return responseData;
};

export const getCampaign = async (clientId: string, id: string): Promise<Campaign> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/campaign/${id}`, {
    method: 'GET',
  });
  return response.json();
};

export const updateCampaign = async (
  clientId: string,
  id: string,
  data: Partial<Campaign>
): Promise<Campaign> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/campaign/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

// Email template generation
export const generateEmailTemplate = async (
  clientId: string,
  campaignId: string,
  personaId: string,
  hookId?: string
): Promise<GenerateEmailTemplateResponse> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/campaign/${campaignId}/generate_email_template`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ persona_id: personaId, hook_id: hookId }),
    }
  );
  return response.json();
};

// Email template generation
export const generateEmailTemplateForConversation = async (data: {
  description: string;
  type: string;
  sender_name?: string;
  sender_email?: string;
  recipients?: Recipient[];
}) => {
  const response = await fetchWithAuth('/api/campaign/generate_email_template', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const startCampaign = async (clientId: string, id: string): Promise<Campaign> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/campaign/${id}/start`, {
    method: 'POST',
  });
  return response.json();
};

export const deleteCampaignContact = async (
  clientId: string,
  campaignId: string,
  contactId: string
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/campaign/${campaignId}/contact/${contactId}`,
    {
      method: 'DELETE',
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const deleteCampaignCompany = async (
  clientId: string,
  campaignId: string,
  companyId: string
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/campaign/${campaignId}/company/${companyId}`,
    {
      method: 'DELETE',
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const getConsole = async ({
  clientId,
  campaignId,
  workflowId,
  startingAfter,
  status,
  search,
  contactId,
  startDate,
  endDate,
  selectedNode,
  nextTriggerStart,
  nextTriggerEnd,
}: {
  clientId: string;
  campaignId: string;
  workflowId: string;
  startingAfter?: string;
  status?: string;
  search?: string;
  contactId?: string;
  startDate?: string;
  endDate?: string;
  selectedNode?: string;
  nextTriggerStart?: string;
  nextTriggerEnd?: string;
}): Promise<ListResponse<ConsoleEvent>> => {
  const params = new URLSearchParams();
  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }

  if (search) {
    params.append('search', search);
  }
  if (status === 'terminated') {
    params.append('termination_status', 'true');
  } else if (status && status !== 'all') {
    params.append('execution_status', status);
  }

  if (contactId) {
    params.append('contact_id', contactId);
  }

  if (startDate) {
    params.append('executed_at_start', startDate);
  }

  if (endDate) {
    params.append('executed_at_end', endDate);
  }

  if (nextTriggerStart) {
    params.append('next_trigger_at_start', nextTriggerStart);
  }

  if (nextTriggerEnd) {
    params.append('next_trigger_at_end', nextTriggerEnd);
  }

  if (selectedNode) {
    params.append('node_id', selectedNode);
  }

  const response = await fetchWithAuth(
    `/api/client/${clientId}/campaign/${campaignId}/workflow/${workflowId}/console?${params.toString()}`,
    {
      method: 'GET',
    }
  );
  return response.json();
};

export const testEmail = async (
  clientId: string,
  campaignId: string,
  workflowId: string,
  request: TestEmailRequest
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/campaign/${campaignId}/workflow/${workflowId}/test_email`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.json();
};
export const getCampaignCount = async (
  clientId: string,
  search?: string,
  filterSearchParams?: URLSearchParams
): Promise<{
  total: number;
  ended: number;
  draft: number;
  paused: number;
  running: number;
  scheduled: number;
}> => {
  const params = new URLSearchParams(filterSearchParams || {});
  if (search) {
    params.set('search', search);
  }
  // Remove tab-specific filters
  params.delete('campaign_status');

  const response = await fetchWithAuth(
    `/api/client/${clientId}/campaign/count?${params.toString()}`,
    {
      method: 'GET',
    }
  );
  return response.json();
};

export const getCampaignInsight = async (
  clientId: string,
  campaignId: string
): Promise<CampaignInsight> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/campaign/${campaignId}/insight`, {
    method: 'GET',
  });
  return response.json();
};

export const getConsoleCount = async (
  clientId: string,
  campaignId: string,
  workflowId: string,
  search?: string,
  selectedNode?: string,
  filterSearchParams?: URLSearchParams
): Promise<{
  total: number;
  completed: number;
  failed: number;
  pending: number;
  skipped: number;
  terminated?: number;
}> => {
  const params = new URLSearchParams(filterSearchParams || {});
  if (search) {
    params.set('search', search);
  }

  if (selectedNode) {
    params.set('node_id', selectedNode);
  }

  params.delete('execution_status');

  const response = await fetchWithAuth(
    `/api/client/${clientId}/campaign/${campaignId}/workflow/${workflowId}/console/count?${params.toString()}`,
    {
      method: 'GET',
    }
  );
  return response.json();
};
